import { supabase } from '../lib/supabase';
import { extractEdgeFunctionError } from '../utils/edgeFunctionUtils';

// Role hierarchy levels (0 = highest authority, 7 = lowest)
const ROLE_LEVELS = {
  primary_owner: 0,
  owner: 1,
  executive: 2,
  general_manager: 3,
  department_head: 4,
  manager: 5,
  supervisor: 6,
  staff: 7,
  contractor: 7
};

// All available roles with labels
const ALL_ROLES = [
  { value: 'owner', label: 'Owner', level: 1 },
  { value: 'executive', label: 'Executive', level: 2 },
  { value: 'general_manager', label: 'General Manager', level: 3 },
  { value: 'department_head', label: 'Department Head', level: 4 },
  { value: 'manager', label: 'Manager', level: 5 },
  { value: 'supervisor', label: 'Supervisor', level: 6 },
  { value: 'staff', label: 'Staff', level: 7 },
  { value: 'contractor', label: 'Contractor', level: 7 }
];

export const workforceService = {
  // Get all workforce members for a company
  async getCompanyWorkforce(companyId) {
    const { data, error } = await supabase?.from('company_users')?.select(`
        id,
        role,
        is_active,
        created_at,
        company_alias,
        user_id,
        user_profiles (
          id,
          email,
          full_name,
          username,
          account_type,
          avatar_url
        )
      `)?.eq('company_id', companyId)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(member => ({
      id: member?.id,
      role: member?.role,
      isActive: member?.is_active,
      createdAt: member?.created_at,
      companyAlias: member?.company_alias,
      userId: member?.user_id,
      email: member?.user_profiles?.email,
      fullName: member?.user_profiles?.full_name,
      username: member?.user_profiles?.username,
      accountType: member?.user_profiles?.account_type,
      avatarUrl: member?.user_profiles?.avatar_url
    }));
  },

  // Get employees only (exclude contractors)
  async getCompanyEmployees(companyId) {
    const allMembers = await this.getCompanyWorkforce(companyId);
    return allMembers?.filter(member => member?.role !== 'contractor');
  },

  // Get contractors only
  async getCompanyContractors(companyId) {
    const allMembers = await this.getCompanyWorkforce(companyId);
    return allMembers?.filter(member => member?.role === 'contractor');
  },

  // Update member status (uses RPC function with proper validation)
  async updateMemberStatus(memberId, isActive, companyId) {
    const { data, error } = await supabase.rpc('change_user_status', {
      p_member_id: memberId,
      p_is_active: isActive,
      p_company_id: companyId
    });

    if (error) {
      console.error('Error changing status:', error);
      throw new Error('Failed to change status');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to change status');
    }

    return {
      id: data.member_id,
      isActive: data.is_active,
      action: data.action
    };
  },

  // Update member role (uses RPC function with proper validation)
  async updateMemberRole(memberId, newRole, companyId) {
    console.log('🔄 Changing role:', { memberId, newRole, companyId });

    const { data, error } = await supabase.rpc('change_user_role', {
      p_member_id: memberId,
      p_new_role: newRole,
      p_company_id: companyId
    });

    console.log('📊 RPC Response:', { data, error });

    if (error) {
      console.error('❌ Supabase RPC Error:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw new Error(error.message || 'Failed to change role');
    }

    if (!data?.success) {
      console.error('❌ Business Logic Error:', data?.error);
      throw new Error(data?.error || 'Failed to change role');
    }

    console.log('✅ Role changed successfully:', data);
    return {
      id: data.member_id,
      oldRole: data.old_role,
      newRole: data.new_role
    };
  },

  // ============================================
  // NEW: Workforce Creation Functions
  // ============================================

  /**
   * Get the hierarchy level for a role (0 = highest, 7 = lowest)
   */
  getRoleLevel(role) {
    return ROLE_LEVELS[role] ?? 99;
  },

  /**
   * Check if a creator role can create a target role
   * Returns true if creator level < target level (lower number = higher authority)
   */
  canCreateRole(creatorRole, targetRole) {
    const creatorLevel = this.getRoleLevel(creatorRole);
    const targetLevel = this.getRoleLevel(targetRole);
    return creatorLevel < targetLevel;
  },

  /**
   * Get list of roles that the user can create based on their role
   * @param {string} userRole - The current user's role
   * @param {string} type - 'employee' or 'contractor'
   * @returns {Array} Array of { value, label } objects
   */
  getRolesUserCanCreate(userRole, type = 'employee') {
    const userLevel = this.getRoleLevel(userRole);

    if (type === 'contractor') {
      // For contractor type, only return contractor role if user can create it
      if (userLevel < 7) {
        return [{ value: 'contractor', label: 'Contractor' }];
      }
      return [];
    }

    // For employees, filter roles below user's level (excluding contractor)
    return ALL_ROLES.filter(role =>
      role.value !== 'contractor' && role.level > userLevel
    );
  },

  /**
   * Check if a username is available
   * @param {string} username - Username without @mervo suffix
   * @returns {Promise<{available: boolean, error?: string, masterId?: string}>}
   */
  async checkUsernameAvailable(username) {
    const { data, error } = await supabase.rpc('check_username_available', {
      p_username: username
    });

    if (error) {
      console.error('Username check error:', error);
      throw new Error('Failed to check username availability');
    }

    return {
      available: data?.available ?? false,
      error: data?.error,
      masterId: data?.master_id,
      username: data?.username
    };
  },

  /**
   * Generate a secure random password
   * @returns {string} 12-character password with letters, numbers, and symbols
   */
  generatePassword() {
    const lowercase = 'abcdefghijkmnpqrstuvwxyz'; // Excluding l, o to avoid confusion
    const uppercase = 'ABCDEFGHJKMNPQRSTUVWXYZ'; // Excluding I, L, O
    const numbers = '23456789'; // Excluding 0, 1 to avoid confusion
    const symbols = '!@#$%&*';

    let password = '';

    // Ensure at least one of each type
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill remaining 8 characters
    const allChars = lowercase + uppercase + numbers + symbols;
    for (let i = 0; i < 8; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  },

  /**
   * Validate password meets requirements
   * @param {string} password
   * @returns {{valid: boolean, error?: string}}
   */
  validatePassword(password) {
    if (!password || password.length < 8) {
      return { valid: false, error: 'Password must be at least 8 characters' };
    }
    if (!/[a-zA-Z]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one letter' };
    }
    if (!/[0-9]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one number' };
    }
    return { valid: true };
  },

  /**
   * Create a new workforce member (employee or contractor)
   * Calls the Edge Function which handles both new users and existing user invites
   *
   * @param {Object} params
   * @param {string} params.companyId - Target company UUID
   * @param {string} params.fullName - Full name of the new member
   * @param {string} params.email - Email address
   * @param {string} params.username - Username (without @mervo)
   * @param {string} params.role - Role to assign
   * @param {string} params.password - Password for the account
   * @returns {Promise<{success: boolean, type: 'created'|'invited', data: Object, error?: string}>}
   */
  async createWorkforceMember({ companyId, fullName, email, username, role, password }) {
    // Validate password first
    const passwordCheck = this.validatePassword(password);
    if (!passwordCheck.valid) {
      return { success: false, error: passwordCheck.error };
    }

    // Get the session token for authorization
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: 'Not authenticated' };
    }

    // Call the Edge Function
    const { data, error } = await supabase.functions.invoke('create-workforce-member', {
      body: {
        companyId,
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        username: username.trim().toLowerCase(),
        role,
        password
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to create workforce member');
      return { success: false, error: errorMessage };
    }

    return data;
  }
};